import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Observable, Subject } from 'rxjs';
import { first, take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { CharityPartner } from '../../models/content';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { PicalunaSiteService } from '../../services/picaluna-site.service';

import { ContentCharityComponent } from './content-charity.component';

/**
 * Content module — Charity partners list. Clone of ContentRegionListComponent:
 * persists via accountService.patchContent('charities', ...) (the RBAC-gated
 * /account/content route), HARD delete (the route replaces the whole collection;
 * use the `published` toggle to hide without deleting).
 */
@Component({
  selector: 'app-content-charity-list',
  templateUrl: './content-charity-list.component.html',
})
export class ContentCharityListComponent implements OnInit, OnDestroy {
  /** True until ensureFullAccount() resolves. account$ replays a cached,
   *  content-STRIPPED account instantly, so gating on the observable alone
   *  renders an empty table instead of the loading state. */
  accountLoading = true;

  private unsubscribe$ = new Subject<void>();
  account$: Observable<Account>;

  /** Slug currently publishing, so only that row spins. */
  publishingSlug: string | null = null;

  constructor(
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    private picalunaSiteService: PicalunaSiteService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    // The default account fetch omits the content subtree (payload diet);
    // load it on demand so this editor has data to show.
    this.accountService.ensureFullAccount().subscribe({
      next: () => (this.accountLoading = false),
      error: () => (this.accountLoading = false),
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  charitiesAsList(content: any): CharityPartner[] {
    const data = content?.charities;
    if (!data) return [];
    const list = Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean);
    return _.sortBy(list as CharityPartner[], (c) => c?._meta?.index ?? Number.MAX_SAFE_INTEGER);
  }

  onAddCharity(): void {
    const account = this.snapshot();
    if (!account) return;
    const existing = this.charitiesAsList(account.data?.content);
    const seed: CharityPartner = {
      _meta: { id: `id-charity-${this.shortId()}`, index: this.nextIndex(existing) },
      slug: '',
      name: '',
      published: true,
    };
    this.openEditor(seed, true);
  }

  /**
   * Publish just this charity partner to the website.
   *
   * Ships only this record — every other piece of site content stays exactly
   * as deployed. Bidirectional like the blog and region actions: an unpublished
   * charity is REMOVED from the site by this, because the scope means "make the
   * site match Conductor for this item".
   */
  onPublishCharity(charity: CharityPartner): void {
    const slug = (charity?.slug || '').trim();
    if (!slug || this.publishingSlug) {
      return;
    }

    const isLive = charity.published !== false;
    const name = charity.name || slug;

    const modal = this.modalService.generic({
      title: isLive ? 'Publish this charity partner?' : 'Remove this charity partner from the site?',
      copy: isLive
        ? [
            `This publishes "${name}" to the website preview.`,
            'Only this charity partner is published — everything else on the site stays as it is now.',
          ]
        : [
            `"${name}" is hidden, so publishing it will REMOVE it from the website.`,
            'Turn Published on first if you meant to put it live.',
          ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        {
          label: isLive ? 'Publish this partner' : 'Remove from site',
          key: 'publish',
          class: isLive ? 'p-button-success' : 'p-button-danger',
        },
      ],
    });

    modal?.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (key: string) => {
        if (key !== 'publish') {
          return;
        }

        this.publishingSlug = slug;

        this.picalunaSiteService
          .publish(this.publishScope('charities', charity))
          // take(1), not takeUntil: the publish DISPATCH must outlive the
          // dialog — takeUntil aborted it mid-flight and skipped the
          // publishedSlug breadcrumb, so the NEXT rename lost its redirect.
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.publishingSlug = null;
              // Remember what shipped, so a later rename can take this page
              // down instead of leaving the old URL live.
              this.recordPublishedSlug(charity._meta.id, slug);
              this.modalService.generic({
                title: 'Publishing started',
                copy: [
                  `"${name}" is being published. It takes a few minutes.`,
                  'Track progress on the Website page, then publish to production from there when the preview looks right.',
                ],
                buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
              });
            },
            error: (err) => {
              this.publishingSlug = null;
              this.modalService.error(err?.error?.error || err);
            },
          });
      },
    });
  }

  onEditCharity(charity: CharityPartner): void {
    this.openEditor(_.cloneDeep(charity), false);
  }

  onDeleteCharity(charity: CharityPartner): void {
    const modal = this.modalService.generic({
      title: 'Remove charity partner',
      copy: [
        `Remove "${charity.name || charity._meta.id}"? Its page will stay on picaluna.com until the NEXT publish — run one when ready. To hide it without deleting, uncheck "Published" instead.`,
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Remove', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });
    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() !== 'confirm') return;
        const account = this.snapshot();
        if (!account?.data?.content?.charities || !Array.isArray(account.data.content.charities)) return;
        account.data.content.charities = (account.data.content.charities as CharityPartner[]).filter(
          (c) => c?._meta?.id !== charity._meta.id
        );
        this.persistRemove(charity._meta.id);
      },
    });
  }

  private openEditor(charity: CharityPartner, isNew: boolean): void {
    const ref = this.dialogService.open(ContentCharityComponent, {
      // Esc would close the editor and silently discard unsaved changes.
      closeOnEscape: false,
      header: isNew ? 'Add charity partner' : `Edit ${charity.name || 'charity partner'}`,
      width: '90vw',
      baseZIndex: 11000,
      data: {
        charity,
        isNew,
        // Editor-performed save: it closes only once the server confirms
        // (Save spins meanwhile), so a publish cannot race the write.
        save: (c: any) => this.accountService.patchContentItem('charities', c).pipe(take(1)),
      },
    });
    ref.onClose.subscribe({
      next: (result: { saved: boolean; charity: CharityPartner; persisted?: boolean } | undefined) => {
        if (!result?.saved) return;
        const account = this.snapshot();
        if (!account) return;
        if (!account.data.content) account.data.content = {};
        if (!Array.isArray(account.data.content.charities)) account.data.content.charities = [];
        const arr = account.data.content.charities as CharityPartner[];
        const i = arr.findIndex((c) => c?._meta?.id === result.charity._meta.id);
        if (i >= 0) arr[i] = result.charity;
        else arr.push(result.charity);
        if (!result.persisted) this.persistItem(result.charity);
      },
    });
  }

  /**
   * Writes are take(1) with NO takeUntil.
   *
   * takeUntil(unsubscribe$) on a write ABORTS the in-flight request when the
   * modal closes, so a save or delete could be silently cancelled by closing
   * the dialog that started it — and it completed the stream empty, so first()
   * threw EmptyError ("no elements in sequence"). A content write must outlive
   * its dialog, like recordPublishedSlug below. Errors are surfaced rather than
   * swallowed: a rejected save that leaves the row on screen reads as success.
   */
  // Per-item persistence: sends ONLY the edited/removed record, so two
  // content managers working in different charities can't clobber each other.
  private persistItem(item: any): void {
    this.accountService
      .patchContentItem('charities', item)
      .pipe(take(1))
      .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
  }

  private persistRemove(id: string): void {
    this.accountService
      .removeContentItem('charities', id)
      .pipe(take(1))
      .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
  }

  private snapshot(): Account | undefined {
    let s: Account | undefined;
    this.accountService.account$.pipe(first()).subscribe({ next: (a) => (s = a) });
    return s;
  }
  private nextIndex(existing: CharityPartner[]): number {
    return existing.reduce((acc, c) => {
      const i = c?._meta?.index;
      return typeof i === 'number' && i > acc ? i : acc;
    }, -10) + 10;
  }
  private shortId(): string {
    return Math.random().toString(36).slice(2, 10);
  }

  /**
   * The scope for a per-item publish, taking the OLD page down when the slug
   * changed since it was last published.
   *
   * The scoped merge keys on slug, so a renamed item's previous record is not in
   * scope and gets carried forward untouched while the new one is appended —
   * leaving both URLs live. Sending the old token too resolves it to a takedown,
   * because an id in scope that Conductor no longer has is removed.
   */
  private publishScope(type: string, item: { slug?: string; publishedSlug?: string }): string {
    const slug = (item.slug || '').trim();
    const prev = (item.publishedSlug || '').trim();
    return prev && prev !== slug ? `${type}:${prev} ${type}:${slug}` : `${type}:${slug}`;
  }


  /**
   * Record the slug this item just shipped under.
   *
   * Re-reads the account FIRST. patchContentItem sends the whole record and the
   * server's upsert is delete-then-rewrite with no version check, so writing the
   * copy this dialog has been holding would revert anything another content
   * manager changed since it loaded. Fetching immediately before the write
   * shrinks that window to the request itself.
   *
   * Best-effort throughout: this is a breadcrumb for a later rename, and it must
   * never surface an error over a publish that already succeeded.
   */
  private recordPublishedSlug(metaId: string, slug: string): void {
    // NOT gated on takeUntil(unsubscribe$). This is a fire-and-forget
    // breadcrumb that must outlive the dialog: closing the list during the
    // fetch cancelled the whole chain, so the slug was never recorded and the
    // next rename could not take the old page down. first() completes both
    // observables on their own, so nothing leaks.
    this.accountService
      .get(true)
      .pipe(first())
      .subscribe({
        next: () => {
          const account = this.snapshot();
          const rows = account?.data?.content?.charities;
          if (!Array.isArray(rows)) return;
          const fresh = (rows as CharityPartner[]).find((r) => r?._meta?.id === metaId);
          if (!fresh || fresh.publishedSlug === slug) return;
          fresh.publishedSlug = slug;
          this.accountService
            .patchContentItem('charities', fresh)
            .pipe(first())
            .subscribe({ error: () => {} });
        },
        error: () => {},
      });
  }

}
